import React, { useState, useRef, useEffect } from 'react';
import { X, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface TwoFactorModalProps {
  isOpen: boolean;
  pendingToken: string;
  user: { id: string; username: string };
  onSuccess: (token: string, user: any) => void;
  onCancel: () => void;
}

const TwoFactorModal: React.FC<TwoFactorModalProps> = ({
  isOpen,
  pendingToken,
  user,
  onSuccess,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCode('');
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleVerify = async () => {
    const trimmed = code.replace(/\s/g, '');
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${API_BASE_URL}/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pendingToken, code: trimmed }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Verification failed');
      onSuccess(data?.data?.token || data.token, data?.data?.user || data.user);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onCancel}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="bg-white w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden pointer-events-auto border border-gray-100 mx-4">
              {/* Header */}
              <div className="relative px-8 pt-8 pb-4 text-center">
                <button
                  onClick={onCancel}
                  className="absolute right-6 top-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="mb-4 flex justify-center">
                  <div className="w-14 h-14 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg shadow-gray-200">
                    <ShieldCheck className="w-7 h-7 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Two-factor auth</h2>
                <p className="text-gray-500 mt-1 text-sm font-medium">
                  Enter the 6-digit code from your authenticator app
                  {user?.username ? ` for @${user.username}` : ''}.
                </p>
              </div>

              {/* Body */}
              <div className="px-8 pb-8 pt-4">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600 font-medium">{error}</p>
                  </motion.div>
                )}

                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  value={code}
                  onChange={e => { setCode(e.target.value.replace(/[^0-9A-Z]/gi, '')); setError(null); }}
                  onKeyDown={e => e.key === 'Enter' && handleVerify()}
                  placeholder="000000"
                  className="w-full text-center text-3xl tracking-widest font-bold py-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-300 focus:bg-white focus:border-gray-900 focus:ring-4 focus:ring-gray-100 transition-all outline-none"
                />

                <button
                  onClick={handleVerify}
                  disabled={loading || !code.replace(/\s/g, '')}
                  className="mt-4 w-full bg-gray-900 text-white rounded-xl py-3.5 font-bold text-lg shadow-xl shadow-gray-200 hover:bg-black hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:shadow-none disabled:scale-100"
                >
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Verify'}
                </button>

                <p className="mt-4 text-xs text-center text-gray-400">
                  You can also enter a one-time backup code.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TwoFactorModal;
